package com.hyperether.goodjob.util

import androidx.compose.runtime.MutableState
import androidx.compose.ui.graphics.painter.BitmapPainter
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.log
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.repository.remote.model.FileObject
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.util.Constants.DEFAULT_JOB_ID
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

object UploadManager {
    private val _files = MutableStateFlow<List<Pair<String, FileEntity>>>(emptyList())
    val files: StateFlow<List<Pair<String, FileEntity>>> get() = _files

    private val _jobs = MutableStateFlow<List<Job>>(emptyList())
    val jobs: StateFlow<List<Job>> get() = _jobs

    private val queue = mutableListOf<QueueItem>()
    private var isProcessing = false

    fun downloadJobFiles(
        job: Job?,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null
    ) {
        job?.fileObjects?.let {
            if (it.isNotEmpty())
                for (fileObject in it)
                    fileObject?.let {
                        downloadJobFile(fileObject, job.id!!, repository, showLoader)
                    }
            log.d("UploadManager") { "downloadJobFiles fileObjectsList" }
        } ?: run {
            log.d("UploadManager") { "downloadJobFiles fileObjectsList: EMPTY" }
        }
    }

    fun updateFile(
        fileId: String,
        byteArray: ByteArray?,
        documentType: DocumentType,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null,
        jobId: String?,
        onComplete: (() -> Unit)? = null,
        onError: ((String) -> Unit)? = null
    ) {
        jobId?.let { jobId ->
            println("Image File Id: $fileId with ${byteArray?.size} bytes")
            showLoader?.value = false
            byteArray?.let { bytes ->
                uploadFile(
                    fileId,
                    jobId,
                    bytes,
                    documentType,
                    repository,
                    showLoader
                ) { fileId ->
                    fileId?.let { fileId ->
                        when (documentType) {
                            DocumentType.JOB_IMAGE,
                            DocumentType.REPORT_FILE_IMAGE,
                            DocumentType.REPORT_SIGNATURE_IMAGE -> {
                                val painter = repository.getImageFromByteArray(bytes)
                                painter?.let { bp ->
                                    updateFile(
                                        fileId,
                                        FileEntity(
                                            fileId = fileId,
                                            jobId = jobId,
                                            documentType = documentType,
                                            painter = bp
                                        )
                                    )
                                }
                            }

                            DocumentType.JOB_PDF,
                            DocumentType.REPORT_FILE_PDF,
                            DocumentType.REPORT_SIGNATURE_PDF -> {
                                //fileManager.saveFile(fileId, bytes)
//                                val existingFilePath = repository.getSavedFilePath(fileId)
//                                updateFile(
//                                    fileId,
//                                    FileEntity(
//                                        fileId = fileId,
//                                        jobId = jobId,
//                                        documentType = documentType,
//                                        path = existingFilePath
//                                    )
//                                )
                            }

                            else -> {}
                        }

                        if (jobId != DEFAULT_JOB_ID && jobId != "" && (documentType == DocumentType.JOB_IMAGE || documentType == DocumentType.JOB_PDF)) {
                            log.d("FILES") { "enqueueJobUpdate fileId:${fileId}" }
                            enqueueJobUpdate(
                                jobId,
                                fileId,
                                repository,
                                showLoader,
                                onSuccess = {
                                    onComplete?.invoke()
                                },
                                onError = {
                                    onError?.invoke(it)
                                })
                        } else {
                            if (jobId == "")
                                log.e { "jobId == \"\"" }
                            onComplete?.invoke()
                        }
                    }
                }
            }
        } ?: run {
            onError?.invoke(stringResourcePlain(Res.string.unknown_error))
        }
    }

    fun uploadFile(
        fileId: String,
        jobId: String,
        byteArray: ByteArray,
        documentType: DocumentType,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null,
        onFinish: (String?) -> Unit = {}
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            val user = repository.getUser()
            updateFile(
                fileId,
                FileEntity(
                    fileId = fileId,
                    jobId = jobId,
                    documentType = documentType,
                    uploadStatus = UploadStatus.LOADING,
                    fileObject = FileObject(
                        documentFileId = fileId,
                        createdBy = user?.getName(),
                        profileImageFileId = null,
                        createdAt = DateTimeUtil.formatInstantToCustomFormat(Clock.System.now()), // TODO: this time should be valid ONLY for new files
                        userId = user?._id
                    )
                )
            )
            val result = repository.uploadFile(byteArray, fileId)
            log.d("FILES") { "uploadFile fileId:${fileId}" }
            when (result) {
                is Resource.Success -> {
                    println(result)
                    result.data?.fileId?.let { newFileId ->
                        removeFile(fileId)
                        updateFile(
                            newFileId,
                            FileEntity(
                                fileId = newFileId,
                                jobId = jobId,
                                documentType = documentType,
                                uploadStatus = UploadStatus.UPLOADED,
                                fileObject = FileObject(
                                    documentFileId = newFileId,
                                    createdBy = user?.getName(),
                                    profileImageFileId = null,
                                    createdAt = DateTimeUtil.formatInstantToCustomFormat(Clock.System.now()), // TODO: this time should be valid ONLY for new files
                                    userId = user?._id
                                )
                            )
                        )
                        onFinish(newFileId)
                    }
                }

                is Resource.Error -> {
                    updateFile(
                        fileId,
                        FileEntity(
                            fileId = fileId,
                            jobId = jobId,
                            documentType = documentType,
                            uploadStatus = UploadStatus.ERROR
                        )
                    )
                    println(result)
                }
            }
            onFinish(null)
        }
    }

    fun enqueueJobUpdate(
        jobId: String,
        newFileId: String,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        queue.add(
            QueueItem(jobId, newFileId, repository, showLoader, onSuccess, onError)
        )
        processQueue()
    }

    private fun processQueue() {
        if (isProcessing || queue.isEmpty()) return
        isProcessing = true
        val item = queue.removeAt(0)
        // Collect all items with the same jobId
        val sameJobItems = mutableListOf(item)
        val iterator = queue.iterator()
        while (iterator.hasNext()) {
            val nextItem = iterator.next()
            if (nextItem.jobId == item.jobId) {
                sameJobItems.add(nextItem)
                iterator.remove()
            }
        }
        val allFileIds = sameJobItems.map { it.newFileId }.distinct()
        updateJobWithFiles(
            item.jobId,
            allFileIds,
            item.repository,
            item.showLoader,
            onSuccess = {
                sameJobItems.forEach { it.onSuccess() }
                isProcessing = false
                processQueue()
            },
            onError = { errorMsg ->
                sameJobItems.forEach { it.onError(errorMsg) }
                isProcessing = false
                processQueue()
            }
        )
    }

    fun updateJobWithFiles(
        jobId: String,
        newFileIds: List<String>,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            val user = repository.getUser()
            val job = repository.getJobById(jobId).data
            log.d("NOTES") { "updateJobWithFiles: notes: ${job?.noteObjects?.size}" }
            val jobModel = job?.let { Mapper().toJobUpdateRequest(job = it) }
            val fileObjects = newFileIds.map { fileId ->
                FileObject(
                    documentFileId = fileId,
                    createdBy = user?.getName(),
                    profileImageFileId = null,
                    createdAt = DateTimeUtil.formatInstantToCustomFormat(Clock.System.now()),
                    userId = user?._id
                )
            }
            jobModel?.fileObjects = (jobModel.fileObjects ?: emptyList()) + fileObjects
            log.d("FILES") { "updateJobWithFiles: jobModelWithFiles: $jobModel" }
            if (jobModel != null) {
                when (val result = repository.updateJob(jobId, jobRequest = jobModel)) {
                    is Resource.Success -> {
                        result.data?.let { updatedJob ->
                            _jobs.value = _jobs.value.filter { it.id != updatedJob.id } + updatedJob
                            log.d("FILES") { "updateJobWithFiles: updatedJob: $updatedJob notes: ${updatedJob.noteObjects?.size}" }
                            updatedJob.fileObjects?.let {
                                for (file in it) {
                                    file?.let { it1 ->
                                        downloadJobFile(
                                            it1,
                                            jobId,
                                            repository,
                                            showLoader
                                        )
                                    }
                                }
                            }
                        }
                        onSuccess()
                    }


                    is Resource.Error -> {
                        onError(result.text.toString())
                    }
                }
            } else {
                log.d("FILES") { "updateJobWithFiles: jobModel null!!" }
            }
        }
    }

    fun downloadJobFile(
        fileObject: FileObject,
        jobId: String,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null
    ) {
        showLoader?.value = true
        CoroutineScope(Dispatchers.Default).launch {
            val fileId = fileObject.documentFileId ?: return@launch
            val existingFilePath = repository.getSavedFilePath(fileId)
            if (!existingFilePath.isNullOrBlank() && getPlatformChecker().isMobile()) {
                log.d("downloadFile") { "File already exists: $existingFilePath" }

                when (FileUtil().getFileType(fileId)) {
                    FileType.Image -> {
                        val painter = repository.getImage(fileId)
                        if (painter != null) {
                            updateFile(
                                fileId,
                                FileEntity(
                                    fileId = fileId,
                                    jobId = jobId,
                                    documentType = DocumentType.JOB_IMAGE,
                                    painter = painter,
                                    fileObject = fileObject,
                                    uploadStatus = UploadStatus.UPLOADED
                                )
                            )
                        }
                    }

                    FileType.PDF -> {
                        updateFile(
                            fileId,
                            FileEntity(
                                fileId = fileId,
                                jobId = jobId,
                                documentType = DocumentType.JOB_PDF,
                                path = existingFilePath,
                                fileObject = fileObject,
                                uploadStatus = UploadStatus.UPLOADED
                            )
                        )
                    }

                    else -> log.e { "Unsupported file type for existing file" }
                }

                showLoader?.value = false
            } else {
                when (val result = repository.getFile(fileId)) {
                    is Resource.Success -> {
                        val fileType = result.data?.fileType
                        when (fileType) {
                            FileType.Image -> {
                                // val painter = repository.getImage(fileId)
                                val painter = repository.getImageFromByteArray(result.data.fileData)
                                painter?.let {
                                    updateFile(
                                        fileId,
                                        FileEntity(
                                            fileId = fileId,
                                            jobId = jobId,
                                            documentType = DocumentType.JOB_IMAGE,
                                            painter = painter,
                                            fileObject = fileObject,
                                            uploadStatus = UploadStatus.UPLOADED
                                        )
                                    )
                                }
                            }

                            FileType.PDF -> {
                                repository.getSavedFilePath(fileId)?.let {
                                    updateFile(
                                        fileId,
                                        FileEntity(
                                            fileId = fileId,
                                            jobId = jobId,
                                            documentType = DocumentType.JOB_PDF,
                                            path = it,
                                            fileObject = fileObject,
                                            uploadStatus = UploadStatus.UPLOADED
                                        )
                                    )
                                }
                            }

                            else -> log.e { "Unsupported file type" }
                        }
                    }

                    is Resource.Error -> {
//                        errorText.value = "An error occurred while loading files"
//                        showPopUpError.value = true
                        showLoader?.value = false
                        log.d("Download File Error") { "Error downloading file: ${result.text}" }
                    }
                }
                showLoader?.value = false
            }
        }
    }

    fun downloadCompletionReportFiles(
        completionReportResponse: CompletionReportResponse?,
        jobId: String,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null
    ) {
        if (completionReportResponse?.files?.isNotEmpty() == true) {
            completionReportResponse.files?.let {
                for (fileId in it) {
                    downloadCompletionReportFile(
                        fileId,
                        DocumentType.REPORT_FILE,
                        repository,
                        showLoader,
                        onDownloadedImage = { painter ->
                            updateFile(
                                fileId,
                                FileEntity(
                                    fileId = fileId,
                                    jobId = jobId,
                                    documentType = DocumentType.REPORT_FILE_IMAGE,
                                    painter = painter,
                                    uploadStatus = UploadStatus.UPLOADED
                                )
                            )
                        },
                        onDownloadedPdf = { path ->
                            updateFile(
                                fileId,
                                FileEntity(
                                    fileId = fileId,
                                    jobId = jobId,
                                    documentType = DocumentType.REPORT_FILE_PDF,
                                    path = path,
                                    uploadStatus = UploadStatus.UPLOADED
                                )
                            )
                        }
                    )
                }
            }
        }
        if (completionReportResponse?.clientSignature?.isNotEmpty() == true) {
            completionReportResponse.clientSignature.let { fileId ->
                downloadCompletionReportFile(
                    fileId,
                    DocumentType.REPORT_SIGNATURE,
                    repository,
                    showLoader,
                    onDownloadedImage = { painter ->
                        updateFile(
                            fileId,
                            FileEntity(
                                fileId = fileId,
                                jobId = jobId,
                                documentType = DocumentType.REPORT_SIGNATURE_IMAGE,
                                painter = painter,
                                uploadStatus = UploadStatus.UPLOADED
                            )
                        )
                    },
                    onDownloadedPdf = { path ->
                        updateFile(
                            fileId,
                            FileEntity(
                                fileId = fileId,
                                jobId = jobId,
                                documentType = DocumentType.REPORT_SIGNATURE_PDF,
                                path = path,
                                uploadStatus = UploadStatus.UPLOADED
                            )
                        )
                    }
                )
            }
        }
    }

    fun downloadCompletionReportFile(
        fileId: String,
        documentType: DocumentType,
        repository: Repository,
        showLoader: MutableState<Boolean>? = null,
        onDownloadedImage: (BitmapPainter) -> Unit,
        onDownloadedPdf: (String) -> Unit
    ) {
        showLoader?.value = true
        CoroutineScope(Dispatchers.Default).launch {
            val existingFilePath = repository.getSavedFilePath(fileId)
            val fileType = FileUtil().getFileType(fileId)

            if (!existingFilePath.isNullOrBlank() && getPlatformChecker().isMobile()) {
                log.d("downloadFile") { "File already exists: $existingFilePath" }

                when (fileType) {
                    FileType.Image -> {
                        repository.getImage(fileId)?.let {
                            onDownloadedImage(it)
                        }
                    }

                    FileType.PDF -> onDownloadedPdf(existingFilePath)
                    else -> log.e { "Unsupported file type for existing file" }
                }

                showLoader?.value = false
            } else {
                when (val result = repository.getFile(fileId)) {
                    is Resource.Success -> {
                        when (fileType) {
                            FileType.Image -> {
                                result.data?.fileData?.let {
                                    val painter = repository.getImageFromByteArray(it)
                                    painter?.let { bp ->
                                        onDownloadedImage(bp)
                                    }
                                }

                            }

                            FileType.PDF -> {
                                repository.getSavedFilePath(fileId)?.let {
                                    onDownloadedPdf(it)
                                }
                            }

                            else -> log.e { "Unsupported file type" }
                        }
                    }

                    is Resource.Error -> {
                        log.d("Download File Error") { "Error downloading file: ${result.text}" }
                    }
                }
            }

            showLoader?.value = false
        }
    }

    fun updateFile(fileId: String, fileEntity: FileEntity) {
        val currentList = _files.value.toMutableList()
        val index = currentList.indexOfFirst { it.first == fileId }
        if (index == -1) {
            currentList.add(fileId to fileEntity)
        } else {
            val existingFileEntity = currentList[index].second
            currentList[index] = fileId to existingFileEntity.copy(
                fileId = fileId,
                documentType = fileEntity.documentType ?: existingFileEntity.documentType,
                uploadStatus = fileEntity.uploadStatus ?: existingFileEntity.uploadStatus,
                painter = fileEntity.painter ?: existingFileEntity.painter,
                path = fileEntity.path ?: existingFileEntity.path
            )
        }
        _files.value = currentList
        log.d("FILES") { "updateFile fileList:${_files.value.size}" }
    }

    fun removeFile(fileId: String) {
        _files.value = _files.value.filterNot { it.first == fileId }
    }

    fun removeFiles(fileIds: List<String>) {
        _files.value = _files.value
            .filterNot { it.first in fileIds }
            .toMutableList()
    }

    fun removeAllFiles() {
        _files.value = emptyList()
    }

    data class QueueItem(
        val jobId: String,
        val newFileId: String,
        val repository: Repository,
        val showLoader: MutableState<Boolean>? = null,
        val onSuccess: () -> Unit,
        val onError: (String) -> Unit
    )

    fun getReportFileIds(job: Job): List<String> {
        return _files.value
            .filter {
                it.second.jobId == job.id && (it.second.documentType == DocumentType.REPORT_FILE_IMAGE
                        || it.second.documentType == DocumentType.REPORT_FILE_PDF
                        || it.second.documentType == DocumentType.REPORT_FILE)
            }
            .map { it.first }
    }

    fun getSignature(job: Job): String? {
        return _files.value.firstOrNull {
            it.second.jobId == job.id && (it.second.documentType == DocumentType.REPORT_SIGNATURE ||
                    it.second.documentType == DocumentType.REPORT_SIGNATURE_PDF ||
                    it.second.documentType == DocumentType.REPORT_SIGNATURE_IMAGE)
        }?.second?.fileId
    }

    fun getSignatureUploadStatus(job: Job): UploadStatus {
        return _files.value
            .filter {
                it.second.jobId == job.id && (it.second.documentType == DocumentType.REPORT_SIGNATURE ||
                        it.second.documentType == DocumentType.REPORT_SIGNATURE_PDF ||
                        it.second.documentType == DocumentType.REPORT_SIGNATURE_IMAGE)
            }
            .map { it.second }
            .firstOrNull()?.let { fileEntity ->
                fileEntity.uploadStatus ?: UploadStatus.NONE
            } ?: UploadStatus.NONE
    }

    fun isFileUploading(jobId: String): Boolean {
        return _files.value
            .filter { it.second.jobId == jobId && it.second.uploadStatus == UploadStatus.LOADING }
            .map { it.first }
            .isNotEmpty()
    }

    fun isFileUploadError(jobId: String): Boolean {
        return _files.value
            .filter { it.second.jobId == jobId && it.second.uploadStatus == UploadStatus.ERROR }
            .map { it.first }
            .isNotEmpty()
    }

    fun getFileIds(job: Job): List<String> {
        return job.fileObjects?.mapNotNull { it?.documentFileId } ?: emptyList()
    }
}